import React, { ReactElement, useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';

import ContractFormDescription from 'pricing/components/contract/contractFormDescription/ContractFormDescription';
import ContractAmendmentsList from 'pricing/components/contract/contractAmendments/ContractAmendmentsList';
import { contractFormSetFieldsValue } from 'pricing/components/contract/contractForm/utilities/contractFormSetFieldsValue';
import { contractCreate } from 'pricing/components/contract/contractForm/utilities/contractCreate';
import FieldFileUpload from 'pricing/components/contract/contractForm/fields/fieldFileUpload/FieldFileUpload';
import { FieldDate } from 'pricing/components/contract/contractForm/fields/fieldDate/FieldDate';
import FieldContractCondition from 'pricing/components/contract/contractForm/fields/fieldContractCondition/FieldContractCondition';
import FieldContractSearchBreeder from 'pricing/components/contract/contractForm/fields/fieldContractSearchBreeder/FieldContractSearchBreeder';
import ContractTerms from 'pricing/components/contractTerms/ContractTerms';
import { useContractState } from 'pricing/components/contract/useContractState/useContractState';
import { testId } from 'pricing/utilities/test/dataTestId';
import { ContractFormValues } from 'pricing/types';

import { ContractFormStyled, ContractFormTitle } from './ContractForm.styles';

function ContractForm(): ReactElement {
  const [disabled, setDisabled] = useState(true);
  const { state, dispatch } = useContractState();
  const { contract, loading } = state;
  const [form] = Form.useForm();
  const isContractCreated = Boolean(contract?.id);

  const onFinish = async (values: ContractFormValues) => {
    dispatch({ type: 'LOADING_CONTRACT' });
    const respContract = await contractCreate(values);
    dispatch({ type: 'SET_CONTRACT', contract: respContract });
  };

  const handleDisable = () => {
    const hasError = Boolean(
      form.getFieldsError().find((fieldError) => fieldError.errors[0]),
    );
    const allFieldsTouched = form.isFieldsTouched(true);

    setDisabled(!allFieldsTouched || hasError);
  };

  useEffect(() => {
    contract && form.setFieldsValue(contractFormSetFieldsValue(contract));
  }, [contract]);

  return (
    <ContractFormStyled
      layout="vertical"
      form={form}
      onFinish={onFinish}
      onFieldsChange={handleDisable}
      hideRequiredMark
      data-testid={testId.pricingContractForm}
    >
      <Row gutter={18} wrap={false}>
        <Col>
          <FieldFileUpload name="contractFile" form={form} disabled={isContractCreated} />
          <ContractAmendmentsList />
        </Col>
        <Col flex="auto">
          <ContractFormTitle>Contract Information</ContractFormTitle>
          {isContractCreated && <ContractFormDescription contract={contract} />}
          <Row gutter={24}>
            <Col span={8}>
              <FieldContractSearchBreeder form={form} disabled={isContractCreated} />
            </Col>
            <Col span={16}>
              <Form.Item label="Breeder Name" name="breederName" required>
                <Input disabled data-testid={testId.fieldContractBreederName} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <FieldContractCondition disabled={isContractCreated} />
            </Col>
            <Col span={8}>
              <FieldDate
                name="startDate"
                label="Start Date"
                disabled={isContractCreated}
              />
            </Col>
            <Col span={8}>
              <FieldDate name="endDate" label="End Date" disabled={isContractCreated} />
            </Col>
          </Row>

          {!contract ? (
            <Button
              type="primary"
              htmlType="submit"
              disabled={disabled}
              loading={loading}
              data-testid={testId.submitContract}
            >
              Create Contract
            </Button>
          ) : (
            <ContractTerms />
          )}
        </Col>
      </Row>
    </ContractFormStyled>
  );
}

export default ContractForm;
